import { useState } from "react";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { 
  ArrowRight, 
  CheckCircle2, 
  BarChart3, 
  FileText, 
  Users, 
  Wallet, 
  Calendar,
  Shield,
  Zap,
  Clock,
  MessageSquare,
  Bot,
  Briefcase,
  PieChart,
  FolderOpen,
  Star,
  LogIn,
  Headphones,
  Mail,
  BookOpen,
  Puzzle
} from "lucide-react";
import aiConsultantImage from "@/assets/ai-consultant-hero.jpg";
import TierSelectionDialog, { SubscriptionTier } from "@/components/TierSelectionDialog";

const Landing = () => {
  const navigate = useNavigate();
  const [showTierDialog, setShowTierDialog] = useState(false);

  const handleTierSelect = (tier: SubscriptionTier) => {
    localStorage.setItem("selectedTier", JSON.stringify({
      id: tier.id,
      name: tier.name,
      price: tier.price,
      priceId: tier.priceId,
      productId: tier.productId,
      selectedAt: new Date().toISOString(),
    }));
    setShowTierDialog(false);
    navigate("/free-trial");
  };

  const handleGetStarted = () => {
    setShowTierDialog(true);
  };

  const modules = [
    { icon: Users, name: "CRM & Clients", desc: "Lead scoring, funnels & client portal" },
    { icon: FileText, name: "Invoicing", desc: "Estimates, invoices & recurring billing" },
    { icon: Briefcase, name: "Jobs & Projects", desc: "Tasks, milestones & dependencies" },
    { icon: Calendar, name: "Scheduling", desc: "Employee shifts & time tracking" },
    { icon: Wallet, name: "Payroll", desc: "Pay calculations & payment history" },
    { icon: PieChart, name: "Accounting", desc: "Expenses, budgets & profitability" },
    { icon: FolderOpen, name: "Documents", desc: "Contracts, proposals & storage" },
    { icon: MessageSquare, name: "Messaging", desc: "SMS, social & client portal chat" },
    { icon: Bot, name: "AI Consultant", desc: "Business insights & recommendations" },
    { icon: BarChart3, name: "Reports", desc: "Analytics & performance metrics" },
    { icon: Headphones, name: "Support Tickets", desc: "Customer service & issue tracking" },
    { icon: Mail, name: "Email Marketing", desc: "Campaigns, templates & automation" },
    { icon: BookOpen, name: "Knowledge Base", desc: "Help articles & documentation" },
    { icon: Puzzle, name: "Integrations", desc: "Connect your favorite tools" },
    { icon: Shield, name: "Team Roles", desc: "Permissions & multi-org support" },
    { icon: Clock, name: "Automation", desc: "Workflows & auto-messaging" },
  ];

  const stats = [
    { value: "50+", label: "Business Tools" },
    { value: "24/7", label: "AI Assistant" },
    { value: "100%", label: "Cloud Based" },
    { value: "∞", label: "Scalability" },
  ];

  return (
    <div className="min-h-screen bg-sidebar text-sidebar-foreground">
      <TierSelectionDialog
        open={showTierDialog}
        onOpenChange={setShowTierDialog}
        onSelectTier={handleTierSelect}
      />

      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 border-b border-sidebar-border/50 bg-sidebar/80 backdrop-blur-xl">
        <div className="container mx-auto px-3 sm:px-4 py-3 sm:py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="h-8 w-8 sm:h-10 sm:w-10 rounded-xl bg-gradient-to-br from-primary to-accent flex items-center justify-center">
              <Zap className="h-4 w-4 sm:h-5 sm:w-5 text-primary-foreground" />
            </div>
            <span className="text-base sm:text-xl font-bold">Detail Pro Suite</span>
          </div>
          <div className="flex gap-2 sm:gap-3">
            <Button 
              variant="outline" 
              size="sm"
              className="hidden sm:inline-flex border-primary/50 text-primary hover:bg-primary/10"
              onClick={() => navigate("/schedule-demo")}
            >
              Schedule Demo
            </Button>
            <Button 
              variant="ghost"
              size="sm"
              className="text-sidebar-foreground hover:bg-sidebar-accent gap-1 sm:gap-2 text-xs sm:text-sm"
              onClick={() => navigate("/auth")}
            >
              <LogIn className="h-3 w-3 sm:h-4 sm:w-4" />
              <span className="hidden xs:inline">Login</span>
            </Button>
            <Button 
              onClick={handleGetStarted} 
              size="sm"
              className="bg-gradient-to-r from-primary to-accent hover:opacity-90 transition-opacity gap-1 sm:gap-2 text-xs sm:text-sm"
            >
              <span className="hidden sm:inline">Start Free</span>
              <span className="sm:hidden">Start</span>
              <ArrowRight className="h-3 w-3 sm:h-4 sm:w-4" />
            </Button>
          </div>
        </div>
      </nav>

      <main>
        {/* Hero Section */}
        <section className="pt-24 sm:pt-32 pb-12 sm:pb-20 relative overflow-hidden">
          {/* Background effects */}
          <div className="absolute inset-0 overflow-hidden">
            <div className="absolute top-1/4 left-1/4 w-48 sm:w-96 h-48 sm:h-96 bg-primary/20 rounded-full blur-3xl" />
            <div className="absolute bottom-1/4 right-1/4 w-48 sm:w-96 h-48 sm:h-96 bg-accent/20 rounded-full blur-3xl" />
          </div>
          
          <div className="container mx-auto px-3 sm:px-4 relative">
            <div className="max-w-4xl mx-auto text-center space-y-4 sm:space-y-8">
              <div className="inline-flex items-center gap-2 px-3 sm:px-4 py-1.5 sm:py-2 rounded-full bg-sidebar-accent/50 border border-sidebar-border text-xs sm:text-sm">
                <Star className="h-3 w-3 sm:h-4 sm:w-4 text-warning" />
                <span>The complete business operating system</span>
              </div>
              
              <h1 className="text-3xl sm:text-5xl md:text-7xl font-bold leading-tight tracking-tight">
                Run Your Entire Business
                <span className="block bg-gradient-to-r from-primary via-accent to-primary bg-clip-text text-transparent">
                  From One Platform
                </span>
              </h1>
              
              <p className="text-base sm:text-xl text-sidebar-foreground/70 max-w-2xl mx-auto px-2">
                CRM, invoicing, payroll, projects, scheduling, AI insights, and 50+ more tools — 
                all unified in one powerful suite built for service businesses.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center pt-2 sm:pt-4 px-2">
                <Button 
                  size="lg" 
                  onClick={handleGetStarted}
                  className="bg-gradient-to-r from-primary to-accent hover:opacity-90 text-sm sm:text-lg px-6 sm:px-8 py-4 sm:py-6 h-auto"
                >
                  Start 14-Day Free Trial
                  <ArrowRight className="ml-2 h-4 w-4 sm:h-5 sm:w-5" />
                </Button>
                <Button 
                  size="lg" 
                  variant="outline"
                  onClick={() => navigate("/schedule-demo")}
                  className="border-primary/50 text-primary hover:bg-primary/10 text-sm sm:text-lg px-6 sm:px-8 py-4 sm:py-6 h-auto"
                >
                  Watch Demo
                </Button>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-8 pt-8 sm:pt-12 max-w-3xl mx-auto px-2">
                {stats.map((stat, i) => (
                  <div key={i} className="text-center">
                    <div className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
                      {stat.value}
                    </div>
                    <div className="text-xs sm:text-sm text-sidebar-foreground/60 mt-1">{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Modules Grid */}
        <section className="py-12 sm:py-20 relative">
          <div className="container mx-auto px-3 sm:px-4">
            <div className="text-center mb-8 sm:mb-16">
              <h2 className="text-2xl sm:text-3xl md:text-5xl font-bold mb-2 sm:mb-4">
                Everything You Need.{" "}
                <span className="text-sidebar-foreground/50">Nothing You Don't.</span>
              </h2>
              <p className="text-sidebar-foreground/60 text-sm sm:text-lg max-w-2xl mx-auto px-2">
                Replace 10+ separate tools with one integrated platform
              </p>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 sm:gap-4">
              {modules.map((module, i) => (
                <div 
                  key={i}
                  className="group p-3 sm:p-6 rounded-xl sm:rounded-2xl bg-sidebar-accent/30 border border-sidebar-border/50 hover:border-primary/50 hover:bg-sidebar-accent/50 transition-all duration-300"
                >
                  <module.icon className="h-5 w-5 sm:h-8 sm:w-8 text-primary mb-2 sm:mb-4 group-hover:scale-110 transition-transform" />
                  <h3 className="font-semibold mb-0.5 sm:mb-1 text-xs sm:text-base">{module.name}</h3>
                  <p className="text-xs sm:text-sm text-sidebar-foreground/60 line-clamp-2">{module.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Features Highlight with Image */}
        <section className="py-20 bg-sidebar-accent/20">
          <div className="container mx-auto px-4">
            <div className="grid md:grid-cols-2 gap-16 items-center">
              <div className="space-y-8">
                <h2 className="text-3xl md:text-4xl font-bold">
                  AI-Powered Business Intelligence
                </h2>
                <p className="text-lg text-sidebar-foreground/70">
                  Your personal AI consultant analyzes your business data 24/7, 
                  identifying opportunities and providing actionable recommendations.
                </p>
                <ul className="space-y-4">
                  {[
                    "Automated profitability analysis",
                    "Smart client recommendations",
                    "Revenue forecasting",
                    "Expense optimization tips"
                  ].map((item, i) => (
                    <li key={i} className="flex items-center gap-3">
                      <CheckCircle2 className="h-5 w-5 text-success flex-shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
                <Button 
                  onClick={handleGetStarted}
                  className="bg-gradient-to-r from-primary to-accent hover:opacity-90"
                >
                  Try AI Consultant Free
                </Button>
              </div>
              
              <div className="relative">
                <img 
                  src={aiConsultantImage} 
                  alt="AI Business Consultant Interface" 
                  className="rounded-3xl shadow-2xl shadow-primary/20 border border-sidebar-border/50"
                  loading="lazy"
                />
                <div className="absolute -top-4 -right-4 px-4 py-2 rounded-full bg-success text-success-foreground text-sm font-medium">
                  AI-Powered
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Client Portal & Automation */}
        <section className="py-20">
          <div className="container mx-auto px-4">
            <div className="grid md:grid-cols-2 gap-8">
              <div className="p-8 rounded-3xl bg-gradient-to-br from-primary/10 to-transparent border border-sidebar-border/50">
                <Users className="h-12 w-12 text-primary mb-6" />
                <h3 className="text-2xl font-bold mb-4">Client & Employee Portals</h3>
                <p className="text-sidebar-foreground/70 mb-6">
                  Give clients access to view jobs, estimates, invoices, and surveys. 
                  Employees can clock in/out, view schedules, and track earnings.
                </p>
                <ul className="space-y-2 text-sm text-sidebar-foreground/60">
                  <li>• Secure token-based access</li>
                  <li>• Real-time messaging</li>
                  <li>• Estimate approvals</li>
                  <li>• Time tracking</li>
                </ul>
              </div>

              <div className="p-8 rounded-3xl bg-gradient-to-br from-accent/10 to-transparent border border-sidebar-border/50">
                <Zap className="h-12 w-12 text-accent mb-6" />
                <h3 className="text-2xl font-bold mb-4">Smart Automation</h3>
                <p className="text-sidebar-foreground/70 mb-6">
                  Automate repetitive tasks with email templates, SMS reminders, 
                  and multi-channel messaging to SMS, Instagram, Twitter & Facebook.
                </p>
                <ul className="space-y-2 text-sm text-sidebar-foreground/60">
                  <li>• Invoice reminders</li>
                  <li>• Appointment confirmations</li>
                  <li>• Welcome sequences</li>
                  <li>• Follow-up automation</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Testimonials Section */}
        <section className="py-20 bg-sidebar-accent/10">
          <div className="container mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">
                Trusted by Growing Businesses
              </h2>
              <p className="text-sidebar-foreground/60 max-w-2xl mx-auto">
                See what our early adopters are saying
              </p>
            </div>
            <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
              {[
                {
                  quote: "Finally, one platform that does everything. Replaced 8 different tools and saved us hours every week.",
                  author: "Sarah M.",
                  role: "Auto Detailing Shop Owner",
                },
                {
                  quote: "The AI consultant is like having a business advisor on call 24/7. It's spotted trends I would have missed.",
                  author: "Mike R.",
                  role: "Cleaning Service Manager",
                },
                {
                  quote: "Client portal alone has cut our back-and-forth emails by 70%. Clients love the transparency.",
                  author: "Jennifer K.",
                  role: "Landscaping Business Owner",
                },
              ].map((testimonial, i) => (
                <div key={i} className="p-6 rounded-2xl bg-sidebar-accent/30 border border-sidebar-border/50">
                  <div className="flex gap-1 mb-4">
                    {[...Array(5)].map((_, j) => (
                      <Star key={j} className="h-4 w-4 fill-warning text-warning" />
                    ))}
                  </div>
                  <p className="text-sidebar-foreground/80 mb-4 italic">"{testimonial.quote}"</p>
                  <div>
                    <p className="font-semibold">{testimonial.author}</p>
                    <p className="text-sm text-sidebar-foreground/60">{testimonial.role}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Beta Banner */}
        <section className="py-8 bg-primary/10 border-y border-primary/20">
          <div className="container mx-auto px-4 text-center">
            <p className="text-lg">
              <span className="font-semibold text-primary">🚀 Currently in Free Beta</span>
              <span className="text-sidebar-foreground/70 ml-2">
                — Get full access to all features while we perfect the platform
              </span>
            </p>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center p-12 rounded-3xl bg-gradient-to-br from-primary/20 via-sidebar-accent to-accent/20 border border-sidebar-border/50 relative overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-r from-primary/5 to-accent/5" />
              <div className="relative">
                <h2 className="text-3xl md:text-4xl font-bold mb-4">
                  Ready to Simplify Your Business?
                </h2>
                <p className="text-sidebar-foreground/70 mb-8 max-w-xl mx-auto">
                  Join thousands of service businesses using Detail Pro Suite to save time, 
                  increase revenue, and scale operations.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Button 
                    size="lg" 
                    onClick={handleGetStarted}
                    className="bg-gradient-to-r from-primary to-accent hover:opacity-90 text-lg px-8"
                  >
                    Start Free Trial
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                  <Button 
                    size="lg" 
                    variant="outline"
                    onClick={() => navigate("/schedule-demo")}
                    className="border-primary/50 text-primary hover:bg-primary/10 text-lg px-8"
                  >
                    Schedule Demo
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="border-t border-sidebar-border/50 py-12">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-2">
              <div className="h-8 w-8 rounded-lg bg-gradient-to-br from-primary to-accent flex items-center justify-center">
                <Zap className="h-4 w-4 text-primary-foreground" />
              </div>
              <span className="font-semibold">Detail Pro Suite</span>
            </div>
            <div className="flex items-center gap-6 text-sm text-sidebar-foreground/70">
              <a href="/terms" className="hover:text-foreground transition-colors">Terms of Service</a>
              <a href="/privacy" className="hover:text-foreground transition-colors">Privacy Policy</a>
            </div>
            <p className="text-sidebar-foreground/50 text-sm">
              © 2025 Detail Pro Suite. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
